export const DOCUMENT_TABS = {
  POLICIES: 'policies',
  FORMS: 'forms',
  FILES: 'files',
} as const

export const TAB_CONFIG = [
  { id: DOCUMENT_TABS.POLICIES, labelKey: 'clients.documents.tabs.policies' },
  { id: DOCUMENT_TABS.FORMS, labelKey: 'clients.documents.tabs.forms' },
  { id: DOCUMENT_TABS.FILES, labelKey: 'clients.documents.tabs.fileManager' },
]

export const STATUS_FILTER_OPTIONS = {
  [DOCUMENT_TABS.POLICIES]: [
    { value: 'all', labelKey: 'clients.documents.filters.all' },
    { value: 'ACTIVE', labelKey: 'clients.documents.status.accepted' },
    { value: 'PENDING', labelKey: 'clients.documents.status.pending' },
  ],
  [DOCUMENT_TABS.FORMS]: [
    { value: 'all', labelKey: 'clients.documents.filters.all' },
    { value: 'COMPLETED', labelKey: 'clients.documents.status.completed' },
    { value: 'PENDING', labelKey: 'clients.documents.status.pending' },
  ],
}

export const FILE_TYPE_OPTIONS = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'pdf', label: 'PDF' },
  { value: 'doc', label: 'DOC' },
  { value: 'docx', label: 'DOCX' },
  { value: 'txt', label: 'TXT' },
  { value: 'rtf', label: 'RTF' },
  { value: 'csv', label: 'CSV' },
  { value: 'xls', label: 'XLS' },
  { value: 'xlsx', label: 'XLSX' },
]

export const POLICY_STATUS = {
  ACCEPTED: 'accepted',
  PENDING: 'pending',
} as const

export const AGREEMENT_STATUS = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  COMPLETED: 'COMPLETED',
} as const

export const AGREEMENT_STATUS_LABELS = {
  [AGREEMENT_STATUS.PENDING]: 'pending',
  [AGREEMENT_STATUS.ACTIVE]: 'accepted',
  [AGREEMENT_STATUS.EXPIRED]: 'expired',
  [AGREEMENT_STATUS.REVOKED]: 'revoked',
  [AGREEMENT_STATUS.COMPLETED]: 'completed',
} as const

export const FORM_STATUS = {
  COMPLETE: 'complete',
  PENDING: 'pending',
} as const

export const FORM_SUBMISSION_STATUS = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  PENDING_LOWER: 'pending',
  ACCEPTED: 'accepted',
} as const

export const BOOLEAN_VALUES = {
  YES: 'yes',
  NO: 'no',
} as const

export const ENTITY_TYPE = {
  FILE: 'file',
  FORM: 'form',
} as const

export const STATUS_COLOR_MAP = {
  [POLICY_STATUS.ACCEPTED]: 'success.main',
  [POLICY_STATUS.PENDING]: 'warning.main',
  [FORM_STATUS.COMPLETE]: 'success.main',
  [FORM_STATUS.PENDING]: 'warning.main',
  [FORM_SUBMISSION_STATUS.COMPLETED]: 'success.main',
  [FORM_SUBMISSION_STATUS.PENDING]: 'warning.main',
  [AGREEMENT_STATUS.ACTIVE]: 'success.main',
  [AGREEMENT_STATUS.PENDING]: 'warning.main',
  [AGREEMENT_STATUS.EXPIRED]: 'gray.64',
  [AGREEMENT_STATUS.REVOKED]: 'error.main',
} as const

export const TABLE_CONFIG = {
  PAGE_SIZE_OPTIONS: [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ],
} as const

export const TRANSLATION_KEYS = {
  TITLE: 'clients.documents.title',
  ACTIONS: {
    VIEW: 'clients.documents.actions.view',
    DOWNLOAD: 'clients.documents.actions.download',
    DELETE: 'clients.documents.actions.delete',
    RESEND: 'clients.documents.actions.resend',
    SEND_FORM: 'clients.documents.actions.sendForm',
    UPLOAD_FILE: 'clients.documents.actions.uploadFile',
    SIGN: 'clients.documents.actions.sign',
  },
  COLUMNS: {
    POLICY_NAME: 'clients.documents.columns.policyName',
    FORM_NAME: 'clients.documents.columns.formName',
    FILE_NAME: 'clients.documents.columns.fileName',
    FILE_TYPE: 'clients.documents.columns.fileType',
    FILE_SIZE: 'clients.documents.columns.fileSize',
    DATE_TIME: 'clients.documents.columns.dateTime',
    STATUS: 'clients.documents.columns.status',
  },
  STATUS: {
    PREFIX: 'clients.documents.status',
  },
  SEARCH: {
    POLICIES: 'clients.documents.search.policies',
    FORMS: 'clients.documents.search.forms',
    FILES: 'clients.documents.search.files',
  },
  TOASTS: {
    FILE_RENAMED: 'clients.documents.toasts.fileRenamed',
    FILE_RENAME_FAILED: 'clients.documents.toasts.fileRenameFailed',
    FILE_DELETED: 'clients.documents.toasts.fileDeleted',
    FILE_DELETE_FAILED: 'clients.documents.toasts.fileDeleteFailed',
    FILE_VIEW_FAILED: 'clients.documents.toasts.fileViewFailed',
    FILE_DOWNLOAD_STARTED: 'clients.documents.toasts.fileDownloadStarted',
    FILE_DOWNLOAD_FAILED: 'clients.documents.toasts.fileDownloadFailed',
    FORM_DOWNLOAD_STARTED: 'clients.documents.toasts.formDownloadStarted',
    FORM_DOWNLOAD_FAILED: 'clients.documents.toasts.formDownloadFailed',
    AGREEMENT_VIEW_FAILED: 'clients.documents.toasts.agreementViewFailed',
    AGREEMENT_DOWNLOAD_STARTED: 'clients.documents.toasts.agreementDownloadStarted',
    AGREEMENT_DOWNLOAD_FAILED: 'clients.documents.toasts.agreementDownloadFailed',
    AGREEMENT_SENT: 'clients.documents.toasts.agreementSent',
    AGREEMENT_SEND_FAILED: 'clients.documents.toasts.agreementSendFailed',
  },
  VIEW_FORM: {
    SUBMIT_SUCCESS: 'clients.documents.viewForm.submitSuccess',
    SUBMIT_ERROR: 'clients.documents.viewForm.submitError',
  },
  DELETE_DIALOG: {
    FILE: 'deleteFileDialog',
    FORM: 'deleteFormDialog',
  },
  PDF: {
    NO_SIGNATURE: 'clients.documents.pdf.noSignature',
    NO_ANSWER: 'clients.documents.pdf.noAnswer',
    METADATA: {
      CLIENT: 'clients.documents.pdf.client',
      BUSINESS: 'clients.documents.pdf.business',
      DATE: 'clients.documents.pdf.date',
    },
  },
  SIGN_AGREEMENT: {
    TITLE: 'clients.documents.signAgreement.title',
    LOADING: 'clients.documents.signAgreement.loading',
    LOAD_ERROR: 'clients.documents.signAgreement.loadError',
    SIGNATURE_LABEL: 'clients.documents.signAgreement.signatureLabel',
    INITIAL_LABEL: 'clients.documents.signAgreement.initialLabel',
    ADD_SIGNATURE: 'clients.documents.signAgreement.addSignature',
    ADD_INITIAL: 'clients.documents.signAgreement.addInitial',
    DRAW_SIGNATURE: 'clients.documents.signAgreement.drawSignature',
    DRAW_INITIAL: 'clients.documents.signAgreement.drawInitial',
    SIGN: 'clients.documents.signAgreement.sign',
    SIGNING: 'clients.documents.signAgreement.signing',
    SUCCESS: 'clients.documents.signAgreement.success',
    ERROR: 'clients.documents.signAgreement.error',
    ALL_REQUIRED: 'clients.documents.signAgreement.allRequired',
  },
} as const
